<template>
  <div class="">
    <div class="document">
      <es-switch-district-wide-modal
        v-if="showDistrictWideModal"
        @closeModal="cancelDistrictWide"
        @confirm="confirmDistrictWide"/>
      <div class="header">
        <es-header>
          <template #header>
            <div class="header-title">Sites (Step 1 for Elections)</div>
          </template>
          <template #actions>
            <ul>
              <li class="no-border dynamic-width">
                <div class="list-content">
                  <div class="slide-switcher">
                    <label
                      for="switchRoundedSuccess"
                      class="switch-label label has-text-weight-bold">
                      District Wide
                    </label>
                    <input
                      :disabled="
                        loading
                        || !schools.length
                        || electionsPermissions.viewOnly"
                      id="switchRoundedSuccess"
                      type="checkbox"
                      class="switch is-rounded is-small"
                      v-model="isDistrictWide">
                    <label class="slide-switcher-label" for="switchRoundedSuccess"></label>
                  </div>
                </div>
              </li>
            </ul>
          </template>
        </es-header>
      </div>
      <es-loader v-if="loading" style="margin: 6.5rem"/>
      <es-exception
        exceptionMessage="Elections Data Not Available.
          You are trying to access this page before the Population Submission Due Date
          or the Population Data has been submitted/accepted."
        v-else-if="!electionsPermissions.canAccess"
        margin="0"
        style="padding: 6.5rem"/>
      <es-exception
        v-else-if="loadingError"
        exceptionMessage="There was an issue. Please try again or contact your administrator."
        margin="0"
        style="padding: 6.5rem"/>
      <div v-else style="padding: 1rem">
        <div class="has-text-left banner">
          This section displays all sites associated to the Group selected above per CEP provisions.
          The data displayed includes Base Year Population Data and Current Year Population Data.
          You must select a Participation Decision for each site.
          All Sites set to a Cluster or Individual Participation selection will be added to the
          Elections Participation Summary Panel below, where additional actions are required for
          all participating sites.
        </div>
        <div class="table-container">
          <table class="table is-fullwidth is-hoverable" style="margin-bottom: 0.25rem">
            <colgroup>
              <col width="1%">
              <col>
              <col width="6%">
              <col width="6%">
              <col width="6%">
              <col width="6%">
              <col>
              <col width="6%">
              <col width="6%">
              <col width="6%">
              <col>
              <col>
            </colgroup>
            <thead>
              <tr class="header-banner">
                <th colspan="3"></th>
                <th colspan="3" class="header-banner-base">base year population data</th>
                <th></th>
                <th colspan="3" class="header-banner-current">current year population data</th>
                <th colspan="2" rowspan="2" class="header-button-container">
                  <button
                    class="button is-primary banner-refresh-button"
                    :disabled="loading"
                    v-if="(!electionsPermissions.viewOnly && totalCount === 0) || cepSummary.recalculateStatus === 'I'"
                    @click="refresh">
                      {{ this.cepSummary.recalculateStatus === 'I' ? 'Refresh' : 'Load Election Data' }}
                  </button>
                </th>
              </tr>
              <tr>
                <th colspan="3"></th>
                <th colspan="3" class="header-info">
                  Displays the Elections data from the Base Year.
                  If "Continue" is selected below, these numbers will be used.
                </th>
                <th></th>
                <th colspan="3" class="header-info">
                  Displays the Elections data from the current year Population Data.
                  If "Create New" is selected below, the current year Population numbers
                  will be used.
                </th>
              </tr>
            </thead>
            <es-sorter
              :apiSorter="apiSorter"
              :columnHeaders="columnHeaders"
              @updateSorter="updateSorter"/>
            <tbody v-if="!electionsPermissions.viewOnly && totalCount === 0 && (this.cepSummary.recalculateStatus === 'N' || this.cepSummary.recalculateStatus === 'E')">
              <tr>
                <td colspan="12" class="exception-container">
                  <es-exception
                    exceptionMessage="Click the Load Election Data button to get the most current data."
                    margin="0"
                    style="padding: 6.5rem"/>
                </td>
              </tr>
            </tbody>
            <tbody v-else-if="this.cepSummary.recalculateStatus === 'I'">
              <tr>
                <td colspan="12" class="exception-container">
                  <es-exception
                    exceptionMessage="Elections loading is in progress. Click the Refresh button to get the most current data."
                    margin="0"
                    style="padding: 6.5rem"/>
                </td>
              </tr>
            </tbody>
            <tbody v-else-if="!schools.length">
              <tr>
                <td colspan="12" class="exception-container">
                  <es-exception
                    exceptionMessage="No results found."
                    margin="0"
                    style="padding: 6.5rem"/>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <cep-recalculate-row
                v-for="school in schools"
                :key="school.schoolCode"
                :availableClusters="availableClusters"
                :cepSummary="cepSummary"
                :districtWide="cepSummary.districtWide"
                :locationGroupId="locationGroupId"
                :school="school"
                :viewOnly="electionsPermissions.viewOnly"
                @refreshRecalculate="updateParticipationDecision"/>
            </tbody>
          </table>
        </div>
        <a v-if="canLoadMore" style="padding: 0.5rem 0" @click="loadMore">
          Load More
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import qs from 'qs';
import CepRecalculateRow from '@/components/CepRecalculateRow.vue';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsSorter from '@/components/EsSorter.vue';
import EsSwitchDistrictWideModal from '@/components/EsSwitchDistrictWideModal.vue';
import FieldLabels from '@/mixins/FieldLabels';
import NotificationManager from '@/mixins/NotificationManager';
import {
  mapActions,
  mapGetters,
} from 'vuex';

export default {
  mixins: [
    FieldLabels,
    NotificationManager,
  ],
  components: {
    CepRecalculateRow,
    EsException,
    EsHeader,
    EsLoader,
    EsSorter,
    EsSwitchDistrictWideModal,
  },
  props: {
    cepSummary: {
      type: Object,
    },
    locationGroupId: {
      type: [String, Number],
    },
    schoolYear: {
      type: Number,
    },
    sortField: {
      type: String,
      default: 'locationName',
    },
    sortOrder: {
      type: String,
      default: 'ASC',
    },
    electionsPermissions: {
      type: Object,
    },
  },
  data() {
    return {
      availableClusters: [],
      loading: true,
      loadingError: null,
      page: 1,
      pageSize: 13,
      schools: [],
      showDistrictWideModal: null,
      totalCount: null,
      columnHeaders: [],
      apiSorter: {},
      loadAll: false,
    };
  },
  async created() {
    await this.loadPersonTypes();
    this.columnHeaders = [
      {
        header: `${this.getFieldLabel(`PER:${this.defaultPersonType}:DIRECTMATCH:SCHOOL_CODE`)} Code`,
        field: 'localCode',
        order: null,
        sortable: true,
        style: {
          padding: '.1rem 0.75rem',
        },
      },
      {
        header: 'Location Name',
        field: 'locationName',
        order: 'ASC',
        sortable: true,
        style: {
          padding: '.1rem 0.75rem',
        },
      },
      {
        header: 'Current Base Year',
        field: 'currentBaseYear',
        order: null,
        sortable: true,
        style: {
          borderLeft: '1px solid #dee2e6',
          borderRight: '1px solid #dee2e6',
          padding: '.1rem 0.75rem',
        },
      },
      {
        header: 'Total Identified',
        field: 'baseYearTotalIdentified',
        order: null,
        sortable: true,
        style: {
          padding: '.1rem 0.75rem',
        },
      },
      {
        header: 'Total Enrolled',
        field: 'baseYearTotalEnrolled',
        order: null,
        sortable: true,
        style: {
          padding: '.1rem 0.75rem',
        },
      },
      {
        header: 'ISP %',
        field: 'baseYearISP',
        order: null,
        sortable: true,
        style: {
          padding: '.1rem 0.75rem',
        },
      },
      {
        header: '',
        field: null,
        order: null,
        sortable: false,
        style: {
          borderLeft: '1px solid #dee2e6',
          borderRight: '1px solid #dee2e6',
          padding: '.1rem 0.75rem',
        },
      },
      {
        header: 'Total Identified',
        field: 'identifiedCount',
        order: null,
        sortable: true,
        style: {
          padding: '.1rem 0.75rem',
        },
      },
      {
        header: 'Total Enrolled',
        field: 'enrolledCount',
        order: null,
        sortable: true,
        style: {
          padding: '.1rem 0.75rem',
        },
      },
      {
        header: 'ISP %',
        field: 'totalISP',
        order: null,
        sortable: true,
        style: {
          padding: '.1rem 0.75rem',
        },
      },
      {
        header: 'Prior Year Participation',
        field: null,
        order: null,
        sortable: false,
        style: {
          borderLeft: '1px solid #dee2e6',
          borderRight: '1px solid #dee2e6',
          padding: '.1rem 0.75rem',
        },
      },
      {
        header: 'Participation Decision',
        field: null,
        order: null,
        sortable: false,
        style: {
          padding: '.1rem 0.75rem',
        },
      },
    ];
    this.onLoad();
    if (this.cepSummary.districtWide) {
      document.getElementById('switchRoundedSuccess').checked = true;
    }
  },
  watch: {
    $route() {
      this.page = 1;
      this.onLoad();
    },
  },
  computed: {
    ...mapGetters('lookups', [
      'defaultPersonType',
    ]),
    canLoadMore() {
      return (this.page * this.pageSize) < this.totalCount;
    },
    isDistrictWide: {
      get() {
        return this.cepSummary.districtWide;
      },
      set(d) {
        if (d) {
          this.toggleConfirmation();
        } else {
          this.setElectionsScope();
        }
      },
    },
    getSorter() {
      return this.columnHeaders.find(header => header.order);
    },
    searchParams() {
      const params = {};

      if (this.locationGroupId) {
        params.locationGroupId = this.locationGroupId;
      }
      if (this.schoolYear) {
        params.schoolYear = this.schoolYear;
      }
      if (this.getSorter) {
        params.sortField = this.getSorter.field;
        params.sortOrder = this.getSorter.order;
      }
      return params;
    },
    urlParams() {
      const params = {};

      if (this.loadAll) {
        params.pageNum = 1;
        params.pageSize = this.pageSize * this.page;
      } else {
        params.pageNum = this.page ? this.page : undefined;
        params.pageSize = this.pageSize ? this.pageSize : undefined;
      }

      params.sort = JSON.stringify([{
        field: this.sortField,
        order: this.sortOrder,
      }]);
      return params;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadPersonTypes',
    ]),
    cancelDistrictWide() {
      document.getElementById('switchRoundedSuccess').checked = false;
      this.toggleConfirmation();
    },
    confirmDistrictWide() {
      this.setElectionsScope();
      this.toggleConfirmation();
    },
    loadAvailableClusters() {
      return axios.get(`cep/${this.locationGroupId}/elections/${this.schoolYear}/available-clusters`);
    },
    async loadMore() {
      this.page = this.page + 1;
      this.setBusy(true);
      this.loadingError = false;
      try {
        const response = await this.loadSchools();
        response.data.data.forEach(obj => this.schools.push(obj));
      } catch (e) {
        this.loadingError = true;
      } finally {
        this.setBusy(false);
      }
    },
    async loadSchools() {
      this.loadingError = false;
      try {
        const response = await axios
          .get(`cep/${this.locationGroupId}/elections/${this.schoolYear}/schools`,
            {
              params: this.urlParams,
              paramsSerializer(params) {
                return qs.stringify(params, { arrayFormat: 'repeat' });
              },
            });
        [this.apiSorter] = response.data.sorters;

        return response;
      } catch (e) {
        this.loadingError = true;
        return null;
      }
    },
    async onLoad(loadAll) {
      try {
        this.setBusy(true);
        this.loadingError = false;
        this.loadAll = loadAll;

        const responses = await Promise.all([
          this.loadAvailableClusters(),
          this.loadSchools(),
        ]);
        this.availableClusters = responses[0].data;
        this.schools = responses[1].data.data;
        this.totalCount = responses[1].data.totalCount;
        this.$emit('schoolsChanged', this.schools);
      } catch (e) {
        this.loadingError = true;
      } finally {
        this.setBusy(false);
        this.loadAll = false;
      }
    },
    updateParticipationDecision(data) {
      this.$emit('participationDecisionChanged', data);
    },
    setElectionsScope() {
      this.page = 1;
      this.$emit('electionsScopeChanged', !this.cepSummary.districtWide);
    },
    refresh() {
      this.$emit('refresh');
    },
    toggleConfirmation() {
      this.showDistrictWideModal = !this.showDistrictWideModal;
    },
    updateSorter() {
      this.$router.replace({ query: this.searchParams });
    },
    setBusy(busy) {
      this.loading = busy;
    },
  },
};
</script>

<style lang="sass" scoped>
  .slide-switcher
    display: flex
    align-items: center
    label
      margin-right: 0.5rem
      &.slide-switcher-label
        margin-right: 0
        padding-left: 2.2rem
        height: 16px
    .switch-label
      margin-bottom: 0;

  .banner
    font-size: .85rem
    padding-left: 1.5rem
    padding-right: 0.5rem
    margin-bottom: 1rem
    color: #212529

  .table .header-banner th
    border: none
    color: #fff
    height: 40px
    text-transform: uppercase
    text-align: center
    &.header-banner-base
      background-color: #595959
    &.header-banner-current
      background-color: #70ad47
    &.header-button-container
      border-bottom: 1px solid #DEE2e6

  .table th.header-info
    border-right: 1px solid #dee2e6
    border-left: 1px solid #dee2e6

  .table thead tr th
    padding: .1rem 0.75rem
  .exception-container
    text-align: center

  .table-container
    overflow-x: auto
    /deep/ .es-sorter a
      display: flex
      align-items: center
      svg
        margin-left: 5px

</style>
